import { AfterViewInit, ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';

import { LoadingSpinnerComponent, RxAlertModule, RxButtonModule, RxInputModule, RxValidators } from '@roomex/rx-components';
import {
  AuthConfig,
  AuthConfigService,
  AuthFacade,
  AuthLoggerService,
  AuthProvider,
  AuthRoutes,
} from '@rx-applications/shared/auth/data-access';
import { FormUtils } from '@rx-applications/shared/utils-forms';
import { addAbsolutePathPrefix } from '@rx-applications/shared/utils';
import { CommonModule } from '@angular/common';
import { TRANSLATION_SCOPE, translationProvider } from './i18n/translation.provider';
import { TranslocoModule } from '@ngneat/transloco';
import { LoginHeaderComponent } from '@rx-applications/shared/auth/ui';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'rx-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    RxAlertModule,
    ReactiveFormsModule,
    RxInputModule,
    RxButtonModule,
    RouterLink,
    LoginHeaderComponent,
    LoadingSpinnerComponent,
  ],
  providers: [translationProvider],
})
export class LoginComponent extends FormUtils implements OnInit, OnDestroy, AfterViewInit {
  protected readonly TRANSALTION_SCOPE = TRANSLATION_SCOPE;
  protected readonly AuthProvider = AuthProvider;
  protected readonly AuthRoutes = AuthRoutes;
  protected readonly addAbsolutePathPrefix = addAbsolutePathPrefix;

  private readonly authConfigService = inject(AuthConfigService);
  private readonly authLoggerService: AuthLoggerService = inject(AuthLoggerService);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly fb: UntypedFormBuilder = inject(UntypedFormBuilder);

  authFacade: AuthFacade = inject(AuthFacade);
  authConfig: AuthConfig;
  isSsoEnabled = false;
  form: UntypedFormGroup;
  usernameQueryParam: string;

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.initConfiguration();
    this.initForm();
    this.logLandingOnPage();
  }

  ngAfterViewInit() {
    const username = this.route.snapshot.queryParamMap.get('username');
    if (username) {
      this.setUsername(username);
    }
  }

  setUsername(username: string) {
    this.usernameQueryParam = username;
    this.form.patchValue({ username });
  }

  onSubmit() {
    if (this.form.invalid) {
      this.markAllAsTouched(this.form);
      return;
    }

    this.authFacade.login(this.form.value);
  }

  onSsoLoginClick(): void {
    this.authFacade.goToIdentityProvider();
  }

  private initForm(): void {
    this.form = this.fb.group({
      username: new UntypedFormControl('', RxValidators.username),
      password: new UntypedFormControl('', Validators.required),
    });
  }

  private logLandingOnPage(): void {
    const { companyName } = this.authConfig || {};
    this.authLoggerService.landedOnLoginScreen({
      Company: companyName || '',
      sso: this.isSsoEnabled,
    });
  }

  private initConfiguration(): void {
    this.authConfig = this.authConfigService.config;
    this.isSsoEnabled = !!this.authConfigService?.config.sso.ssoEnabled;
  }

  ngOnDestroy() {
    this.authFacade.hideErrorMessage();
  }
}
